import type { Agency, AgencyCreate, AgencyUpdate } from '@/modules/agencies/types/agency'
import type {
  AgencyUser,
  AgencyUserCreate,
  AgencyUserUpdate,
  AgencyUsersQueryParams,
} from '@/modules/agencies/types/agencyUser'
import { baseApi } from '@/store/api/baseApi'

const AGENCIES_PATH = '/platform/agencies'

export const agenciesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgencies: builder.query<Agency[], void>({
      query: () => AGENCIES_PATH,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Agency' as const, id })),
              { type: 'Agency', id: 'LIST' },
            ]
          : [{ type: 'Agency', id: 'LIST' }],
    }),
    getAgency: builder.query<Agency, string>({
      query: (agencyId) => `${AGENCIES_PATH}/${agencyId}`,
      providesTags: (_result, _error, agencyId) => [{ type: 'Agency', id: agencyId }],
    }),
    getAgencyUsers: builder.query<AgencyUser[], AgencyUsersQueryParams>({
      query: ({ agencyId, status, search }) => {
        const params: Record<string, string> = {}
        if (status) {
          params.status = status
        }
        if (search) {
          params.search = search
        }

        return {
          url: `${AGENCIES_PATH}/${agencyId}/users`,
          params,
        }
      },
      providesTags: (result, _error, { agencyId }) =>
        Array.isArray(result)
          ? [
              ...result.map(({ id }) => ({ type: 'AgencyUser' as const, id })),
              { type: 'AgencyUser', id: `LIST-${agencyId}` },
            ]
          : [{ type: 'AgencyUser', id: `LIST-${agencyId}` }],
    }),
    createAgency: builder.mutation<Agency, AgencyCreate>({
      query: (body) => ({
        url: AGENCIES_PATH,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Agency', id: 'LIST' }],
    }),
    updateAgency: builder.mutation<Agency, { agencyId: string; body: AgencyUpdate }>({
      query: ({ agencyId, body }) => ({
        url: `${AGENCIES_PATH}/${agencyId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { agencyId }) => [
        { type: 'Agency', id: agencyId },
        { type: 'Agency', id: 'LIST' },
      ],
    }),
    createAgencyUser: builder.mutation<
      AgencyUser,
      { agencyId: string; body: AgencyUserCreate }
    >({
      query: ({ agencyId, body }) => ({
        url: `${AGENCIES_PATH}/${agencyId}/users`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { agencyId }) => [
        { type: 'AgencyUser', id: `LIST-${agencyId}` },
      ],
    }),
    updateAgencyUser: builder.mutation<
      AgencyUser,
      { agencyId: string; userId: string; body: AgencyUserUpdate }
    >({
      query: ({ agencyId, userId, body }) => ({
        url: `${AGENCIES_PATH}/${agencyId}/users/${userId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { agencyId, userId }) => [
        { type: 'AgencyUser', id: userId },
        { type: 'AgencyUser', id: `LIST-${agencyId}` },
      ],
    }),
    suspendAgency: builder.mutation<Agency, string>({
      query: (agencyId) => ({
        url: `${AGENCIES_PATH}/${agencyId}/suspend`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, agencyId) => [
        { type: 'Agency', id: agencyId },
        { type: 'Agency', id: 'LIST' },
      ],
    }),
    activateAgency: builder.mutation<Agency, string>({
      query: (agencyId) => ({
        url: `${AGENCIES_PATH}/${agencyId}/activate`,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, agencyId) => [
        { type: 'Agency', id: agencyId },
        { type: 'Agency', id: 'LIST' },
      ],
    }),
  }),
})

export const {
  useGetAgenciesQuery,
  useGetAgencyQuery,
  useGetAgencyUsersQuery,
  useCreateAgencyMutation,
  useUpdateAgencyMutation,
  useCreateAgencyUserMutation,
  useUpdateAgencyUserMutation,
  useSuspendAgencyMutation,
  useActivateAgencyMutation,
} = agenciesApi
